"use client";

import { Button } from "@/components/ui/button";
import { ArrowRight } from "lucide-react";

export default function HeroSection() {
  return (
    <section className="relative bg-gradient-to-b from-blue-50 to-white overflow-hidden">
      {/* Magazine-Style Hero Grid */}
      <div className="container mx-auto px-4 pt-[72px] pb-12 sm:pb-14 md:pt-[88px] md:pb-24">
        <div className="grid gap-10 lg:grid-cols-12 lg:gap-8 lg:items-center">
          {/* Left Content - Magazine Editorial Style */}
          <div className="space-y-6 text-center lg:col-span-6 lg:space-y-8 lg:text-left">
            {/* Eyebrow Text - Magazine Style */}
            <div className="flex items-center justify-center gap-3 lg:justify-start lg:gap-4">
              <div className="h-px w-8 bg-black sm:w-12"></div>
              <span className="text-[10px] font-bold uppercase tracking-[0.22em] text-gray-900 sm:text-xs sm:tracking-[0.3em]">
                AI-Powered Storytelling
              </span>
            </div>

            {/* Headline - Bold Magazine Typography */}
            <h1 className="text-4xl font-black leading-[0.95] tracking-tight sm:text-5xl md:text-7xl lg:text-8xl">
              <span className="block text-gray-900">Where</span>
              <span className="block text-gray-900">Stories</span>
              <span className="block bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                Come Alive
              </span>
            </h1>

            {/* Subheadline - Editorial Description */}
            <p className="mx-auto max-w-xl text-base leading-relaxed text-gray-700 font-light sm:text-lg md:text-2xl lg:mx-0">
              Build stories, dramas, anime, Poems and
              Netflix seasons with AI. For kids, teens, parents, teachers,
              and writers.
            </p>

         

            {/* CTA Buttons - Clean & Soft */}
            <div className="flex flex-col gap-3 pt-2 lg:flex-row sm:gap-4 sm:pt-4 lg:justify-start">
              <div className="flex flex-col gap-1 w-full sm:w-auto">
                <Button
                  asChild
                  size="lg"
                  className="min-h-11 w-full bg-blue-600 px-6 py-5 text-base font-semibold text-white transition-all duration-200 border-0 hover:bg-blue-700 hover:shadow-xl sm:w-auto sm:px-8 sm:py-6"
                >
                  <a
                    target="_blank"
                    href="https://app.storly.world/register"
                    className="group"
                    aria-label="Start free on Storly  no credit card required"
                  >
                    Start Free{" "}
                    <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
                  </a>
                </Button>
                <p className="text-center text-xs text-gray-600 lg:text-left">
                  No credit card required
                </p>
              </div>
              <Button
                asChild
                size="lg"
                variant="outline"
                className="min-h-11 w-full hidden lg:flex !shadow-none border border-gray-200 bg-white px-6 py-5 text-base font-semibold text-gray-900 hover:border-blue-600 hover:bg-gray-50 hover:text-blue-600 sm:w-auto sm:px-8 sm:py-[22px]"
              >
                <a href="#pricing" className="group">
                  See Pricing
                </a>
              </Button>
            </div>

       
          </div>

          {/* Right Content - Kid-Friendly Images */}
          <div className="lg:col-span-6">
            <div className="relative mx-auto max-w-[38rem]">
              {/* Main Hero Video */}
              <div className="relative aspect-[5/6] overflow-hidden rounded-[2rem] sm:aspect-[4/5]">
                <video
                  src="/storly-hero.mp4"
                  autoPlay
                  muted
                  loop
                  playsInline
                  className="absolute inset-0 w-full h-full object-cover scale-[1.02]"
                />

                {/* Professional Trust Badge */}
                <div className="absolute right-3 top-3 hidden w-28 overflow-hidden rounded-md border border-white/50 bg-white/80 shadow-[0_25px_60px_rgba(15,23,42,0.18)] backdrop-blur-xl sm:block md:w-32">
                  {/* Gradient Header */}
                  <div className="absolute top-0 left-0 right-0 h-12 bg-gradient-to-b from-blue-50 via-purple-50/50 to-transparent"></div>

                  <div className="relative pt-4 pb-3 px-3">
                    {/* Shield Icon */}
                    <div className="flex justify-center mb-2"></div>

                    {/* Text */}
                    <div className="text-center">
                      <p className="bg-gradient-to-r from-sky-700 via-violet-700 to-orange-500 bg-clip-text text-[11px] font-black leading-tight text-transparent ">
                        Loved by Creators
                      </p>
                      <div className="w-8 h-px bg-gradient-to-r from-transparent via-blue-300 to-transparent mx-auto mt-2"></div>
                    </div>
                  </div>

                  {/* Bottom Accent */}
                </div>

                {/* Bottom Feature Card - Glassmorphism */}
                <div className="absolute bottom-4 left-4 right-4 overflow-hidden rounded-[1.7rem] border border-white/50 bg-white/30 shadow-[0_25px_60px_rgba(15,23,42,0.18)] backdrop-blur-xl sm:bottom-8 sm:left-8 sm:right-8">
                  <div className="absolute inset-0 bg-gradient-to-r from-white/58 via-sky-100/12 to-amber-100/16"></div>
                  <div className="absolute -right-6 bottom-0 h-20 w-20 rounded-full bg-yellow-300/20 blur-3xl"></div>

                  <div className="relative p-4 sm:p-5">
                    <div className="flex items-start gap-3 sm:gap-4">
                      {/* Icon */}
                      <div className="w-12 h-12 rounded-full bg-gradient-to-br from-blue-600 to-purple-600 flex items-center justify-center shadow-lg">
                        <svg
                          className="w-8 h-8 text-white"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                          strokeWidth={2.5}
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"
                          />
                        </svg>
                      </div>

                      {/* Content */}
                      <div className="flex-1">
                        <div className="mb-1"></div>
                        <p className="mb-1 bg-gradient-to-r from-sky-700 via-violet-700 to-orange-500 bg-clip-text text-sm font-black leading-tight text-transparent sm:text-base">
                          Instant Story Creation
                        </p>
                        <p className="text-[11px] leading-relaxed text-slate-700/90 sm:text-xs">
                          Generate unique stories across any genre in seconds
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Accent Elements - Magazine Design Details */}
              <div className="absolute -bottom-4 -right-4 -z-10 h-24 w-24 bg-gradient-to-br from-blue-500 via-purple-500 to-pink-500 opacity-20 blur-3xl sm:-bottom-6 sm:-right-6 sm:h-40 sm:w-40"></div>
              <div className="absolute -left-4 -top-4 -z-10 h-20 w-20 bg-gradient-to-br from-blue-400 to-purple-500 opacity-15 blur-2xl sm:-left-6 sm:-top-6 sm:h-32 sm:w-32"></div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
